import { EDIT_PAGE } from 'context/editor/editorActions';
import { useEffect, useRef, useState, useCallback } from 'react';
import { css } from '@emotion/css';
import balisesStyles from 'pages/CFA/Convention/constants/balisesStyles';
import { generateStyledDiv } from 'pages/CFA/Convention/utils/formatters';

const EDITOR_WIDTH = "calc(794px - 64px)";

const styles = {
  editor: {
    outline: 'none',
    minHeight: 'calc(1123px - 4rem)',
    maxHeight: '1123px',
    overflowY: 'auto',
    width: EDITOR_WIDTH,
    fontFamily: 'inherit',
    lineHeight: 1.5,
    caretColor: '#000000'
  },
  hiddenEditor: {
    outline: 'none',
    position: 'absolute',
    top: '-9999px',
    left: '-9999px',
    overflowY: 'auto',
    width: EDITOR_WIDTH,
    fontFamily: 'inherit',
    lineHeight: 1.5,
  }
};

const TextEditor = ({ content,
  onChange,
  onContentOverflow,
  currentPageIndex,
  isLastPage,
  setEditorRef,
  dispatch,
  state,
  selectedDocument,
  from
}) => {

  const editorRef = useRef(null);
  const hiddenEditorRef = useRef(null);
  const [isComposing, setIsComposing] = useState(false);
  const lastValidContent = useRef(content);
  const isUpdatingRef = useRef(false);
  const maxHeightRef = useRef(1123); // 1123 - 16px*2
  const calculeHeight = useRef(1123);
  const [height, setHeight] = useState(1123);


  useEffect(() => {
    let newEditorHeight = 1123;
    let newMaxHeight = 1123;

    if (currentPageIndex === 1) {
      newEditorHeight -= 120;
      newMaxHeight -= 120;
    }

    if (isLastPage) {
      newEditorHeight -= 170;
      newMaxHeight -= 170;
    }

    setHeight(newEditorHeight);
    calculeHeight.current = newEditorHeight;
    maxHeightRef.current = newMaxHeight;

  }, [currentPageIndex, isLastPage]);

  useEffect(() => {
    if (setEditorRef) {
      setEditorRef(editorRef);
    }
  }, [editorRef]);

  const checkHeight = useCallback(() => {
    if (!hiddenEditorRef.current) return false;

    const currentHeight = hiddenEditorRef.current.getBoundingClientRect().height;

    return currentHeight <= maxHeightRef.current;

  }, [currentPageIndex]);

  const updateContent = useCallback((newHtml) => {
    if (isUpdatingRef.current) return;

    if (!editorRef.current || !hiddenEditorRef.current) return;
    isUpdatingRef.current = true;

    try {
      hiddenEditorRef.current.innerHTML = newHtml;

      if (checkHeight()) {
        lastValidContent.current = newHtml;
        onChange(newHtml);

        // Sauvegarde dans le store
        dispatch({
          type: EDIT_PAGE,
          payload: {
            documentId: selectedDocument?.id,
            pageId: `page${currentPageIndex}`,
            text: newHtml
          }
        });

      } else {
        onContentOverflow();

        if (editorRef.current.innerHTML !== lastValidContent.current) {
          const selection = window.getSelection();
          const range = selection?.getRangeAt(0);

          const previousNode = range?.startContainer;
          const previousOffset = range?.startOffset;

          editorRef.current.innerHTML = lastValidContent.current;

          if (selection && range && previousNode && previousOffset !== undefined) {
            selection.removeAllRanges();
            const newRange = document.createRange();
            newRange.setStart(editorRef.current, 0);
            newRange.setEnd(editorRef.current, 0);
            selection.addRange(newRange);
          }
        }
      }
    } finally {
      isUpdatingRef.current = false;
    }
  }, [checkHeight, onChange, onContentOverflow, dispatch, currentPageIndex]);

  const handleInput = useCallback((e) => {
    if (isComposing || isUpdatingRef.current) return;
    updateContent(e.currentTarget.innerHTML);
  }, [isComposing, updateContent,]);

  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && ['c', 'v'].includes(e.key.toLowerCase())) {
      return;
    }

    if (e.key === 'Enter' && !isComposing) {
      e.preventDefault();
      document.execCommand('insertLineBreak');

      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      range.deleteContents();

      const emptyLine = generateStyledDiv(balisesStyles.normal);

      document.execCommand('insertHTML', false, emptyLine);

      // Et forcer React à détecter le changement
      const event = new Event('input', { bubbles: true });
      document.activeElement.dispatchEvent(event);
    }
  }, [isComposing]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    // Utilise insertText si supporté (préserve la position du curseur)
    if (document.queryCommandSupported('insertText')) {
      document.execCommand('insertText', false, text);
    } else {
      // Fallback pour les vieux navigateurs
      document.execCommand('insertHTML', false, text);
    }
  }, []);


  useEffect(() => {
    if (editorRef.current) {
      const pageId = `page${currentPageIndex}`;
      const documentId = selectedDocument?.id;
      let page = null;
      if (from === "dashboard") {
        page = selectedDocument?.pages.find(p => p?.id === pageId);
      }
      else {
        page = state.documents.find(d => d?.id === documentId)?.pages.find(p => p?.id === pageId);
      }

      if (page) {
        lastValidContent.current = page.text;
        editorRef.current.innerHTML = page.text;
      }
    } else if (editorRef.current && editorRef.current.innerHTML !== content) {
      editorRef.current.innerHTML = content;
    }

    if (hiddenEditorRef.current) {
      hiddenEditorRef.current.innerHTML = content;
    }

    lastValidContent.current = content;
  }, [content, currentPageIndex, selectedDocument]);

  return (
    <>
      <div
        ref={editorRef}
        style={{
          ...styles.editor,
          minHeight: `calc(${height}px - 4rem)`,
          maxHeight: `${height}px`,
          height: `${height}px`,
          borderRadius: "10px",
          flexDirection: "row",
          ...balisesStyles.defaultStyle,
        }}
        className={
          css({
            "& p": {
              margin: 0,
              padding: 0,
            }
          })
        }
        contentEditable={from !== "dashboard"}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => {
          setIsComposing(false);
          if (editorRef.current) {
            updateContent(editorRef.current.innerHTML);
          }
        }}
        suppressContentEditableWarning
        data-placeholder="Commencez à écrire..."
      />

      <div
        ref={hiddenEditorRef}
        style={{
          ...styles.hiddenEditor,
          ...balisesStyles.defaultStyle,
        }}
        className={
          css({
            "& p": {
              margin: 0,
              padding: 0,
            }
          })
        }
        aria-hidden
      />
    </>
  );
};

export default TextEditor;
